import type { TFileWithMeta } from '@components/Files/UploadProvider/types/TFileWithMeta';
import { sanitizeFileName } from '@helpers/sanitizeFileName';

function mediaIdOf(f: TFileWithMeta): string {
  const fromExtra = f.meta.extra?.mediaId;
  if (fromExtra != null && String(fromExtra).trim().length > 0) {
    return String(fromExtra).trim();
  }
  return (f.meta.id ?? '').trim();
}

function identityKeys(f: TFileWithMeta): string[] {
  const keys = new Set<string>();
  const mediaId = mediaIdOf(f);
  if (mediaId.length > 0) {
    keys.add(mediaId);
  }
  const name = sanitizeFileName(f.meta.name || '');
  if (name.length > 0) {
    keys.add(`name:${name}`);
  }
  return [...keys];
}

function sharesIdentity(a: TFileWithMeta, b: TFileWithMeta): boolean {
  const keysB = new Set(identityKeys(b));
  return identityKeys(a).some((key) => keysB.has(key));
}

export function dedupeByMediaIdPreferLast(files: TFileWithMeta[]): TFileWithMeta[] {
  const seen = new Set<string>();
  const result: TFileWithMeta[] = [];
  for (let i = files.length - 1; i >= 0; i--) {
    const f = files[i];
    const id = mediaIdOf(f);
    if (!id) {
      result.push(f);
      continue;
    }
    if (seen.has(id)) {
      continue;
    }
    seen.add(id);
    result.push(f);
  }
  return result.reverse();
}

export function mergeFilesFromServerAndNew(
  filesFromServer: TFileWithMeta[],
  newFiles: TFileWithMeta[],
): TFileWithMeta[] {
  const dedupedNew = dedupeByMediaIdPreferLast(newFiles);
  const filteredNew = dedupedNew.filter((local) => {
    if (!filesFromServer.some((server) => sharesIdentity(local, server))) {
      return true;
    }
    const id = mediaIdOf(local);
    return id.length === 0;
  });
  return [...filesFromServer, ...filteredNew];
}

export function pruneNewFilesPresentOnServer(
  filesFromServer: TFileWithMeta[],
  newFiles: TFileWithMeta[],
): TFileWithMeta[] {
  return newFiles.filter((local) => {
    if (!filesFromServer.some((server) => sharesIdentity(local, server))) {
      return true;
    }
    return mediaIdOf(local).length === 0;
  });
}
